/**
 * Rapid-fire countdown header (spec 29).
 *
 * The ring, the seconds and the hairline bar all read the SAME server-anchored remaining time, so
 * they can never disagree. Under the urgent threshold everything shifts from teal to red — that is
 * the only urgency cue. No shaking, no ticking: the point is to move quickly, not to be alarmed.
 */
import React from 'react';
import { Zap } from 'lucide-react';
import { RAPID_FIRE_URGENT_SECONDS } from './rapidFire';

const RING_RADIUS = 17;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

interface RapidFireTimerProps {
  /** Seconds left on the server deadline; null while it is still being read. */
  remaining: number | null;
  /** Full duration, for the ring and bar proportions. */
  totalSeconds: number;
  /** Questions answered so far in this block. */
  answered: number;
  /** The block's own name — the one the pre-flight card introduced. */
  label?: string;
}

const RapidFireTimer: React.FC<RapidFireTimerProps> = ({
  remaining,
  totalSeconds,
  answered,
  label = 'Quick-fire',
}) => {
  const secondsLeft = remaining ?? totalSeconds;
  const urgent = secondsLeft <= RAPID_FIRE_URGENT_SECONDS;
  const fraction = totalSeconds > 0 ? Math.max(0, Math.min(1, secondsLeft / totalSeconds)) : 0;

  const accentText = urgent ? 'text-red-600' : 'text-gray-800';
  const accentStroke = urgent ? 'stroke-red-500' : 'stroke-blue-600';
  const accentBar = urgent ? 'bg-red-500' : 'bg-blue-600';
  const chip = urgent
    ? 'bg-red-50 border-red-500 text-red-600'
    : 'bg-blue-50 border-blue-500 text-blue-900';

  return (
    <div>
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-[11px] font-bold uppercase tracking-wide whitespace-nowrap ${chip}`}
          >
            <Zap className="w-3 h-3" aria-hidden="true" />
            {label}
          </span>
          <span className="text-sm text-gray-500 font-medium">{answered} answered</span>
        </div>

        {/* aria-live is off on purpose: a per-second announcement would make the section unusable
            with a screen reader. The urgent warning below announces once instead. */}
        <div
          className="flex items-center gap-2.5"
          role="timer"
          aria-live="off"
          aria-label={`${secondsLeft} seconds remaining`}
        >
          <span
            className={`tabular-nums text-2xl font-bold min-w-[2.6ch] text-right transition-colors ${accentText}`}
          >
            {secondsLeft}
          </span>
          <svg className="w-10 h-10 -rotate-90" viewBox="0 0 40 40" aria-hidden="true">
            <circle
              className="fill-none stroke-gray-200"
              strokeWidth="4"
              cx="20"
              cy="20"
              r={RING_RADIUS}
            />
            <circle
              className={`fill-none ${accentStroke} transition-[stroke-dashoffset,stroke] duration-1000 ease-linear`}
              strokeWidth="4"
              strokeLinecap="round"
              cx="20"
              cy="20"
              r={RING_RADIUS}
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={RING_CIRCUMFERENCE * (1 - fraction)}
            />
          </svg>
        </div>
      </div>

      <div className="h-[3px] bg-gray-200">
        <div
          className={`h-full transition-[width] duration-1000 ease-linear ${accentBar}`}
          style={{ width: `${fraction * 100}%` }}
        />
      </div>

      {urgent && (
        <span role="status" className="sr-only">
          Less than {RAPID_FIRE_URGENT_SECONDS} seconds remaining
        </span>
      )}
    </div>
  );
};

export default RapidFireTimer;
